import React from 'react';
import { Check, Loader2, Plus, Trash2, X } from 'lucide-react';
import type { NQMMPreviousPosition, Position } from '../../services/api';
import Modal from '../Modal';
import {
  buildManualNQMMPositionCorrection,
  formatNQMMDate,
  formatNQMMPrice,
} from './nqmmStrategyPresentation';

interface NQMMHistoryCorrectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  historyLoading: boolean;
  historyError: string | null;
  submittingSymbol: string | null;
  previousPositions: NQMMPreviousPosition[];
  currentPositions: Position[];
  addPreviousPosition: (position: Partial<Position> & { symbol: string; quantity: number }) => Promise<void>;
  deletePreviousPosition: (symbol: string) => Promise<void>;
}

const NQMMHistoryCorrectionModal: React.FC<NQMMHistoryCorrectionModalProps> = ({
  isOpen,
  onClose,
  historyLoading,
  historyError,
  submittingSymbol,
  previousPositions,
  currentPositions,
  addPreviousPosition,
  deletePreviousPosition,
}) => {
  const [manualSymbol, setManualSymbol] = React.useState('');
  const [manualQuantity, setManualQuantity] = React.useState('100');
  const [manualPrice, setManualPrice] = React.useState('');

  if (!isOpen) return null;

  const stockPositions = currentPositions.filter(
    (position) => !position.secType || ['STK', 'CFD'].includes(position.secType),
  );

  const handleManualSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const correction = buildManualNQMMPositionCorrection(
      manualSymbol,
      manualQuantity,
      manualPrice,
    );
    if (!correction) return;
    await addPreviousPosition(correction);
    setManualSymbol('');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Correct previous NQMM positions" width="max-w-4xl">
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-xs text-muted">
          <span>Select a current stock position or manually enter a symbol and share count to update NQMM's 10-record position history.</span>
          <button type="button" onClick={onClose} className="text-muted hover:text-white" aria-label="Close"><X size={16} /></button>
        </div>

        {historyError && <div className="rounded-lg border border-rose-400/20 bg-rose-400/5 px-3 py-2 text-xs text-rose-300">{historyError}</div>}

        {historyLoading ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted"><Loader2 size={16} className="mr-2 animate-spin text-neon" /> Loading positions…</div>
        ) : (
          <>
            <form onSubmit={handleManualSubmit} className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-neon">Add custom position record</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="block text-[10px] text-muted mb-1 font-semibold uppercase">Symbol</label>
                  <input
                    type="text"
                    placeholder="e.g. NVDA"
                    value={manualSymbol}
                    onChange={(event) => setManualSymbol(event.target.value.toUpperCase())}
                    className="w-full rounded-lg border border-white/10 bg-black/40 px-2.5 py-1.5 text-xs text-white uppercase placeholder:text-muted/50 focus:border-neon focus:outline-hidden"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-muted mb-1 font-semibold uppercase">Shares / Quantity</label>
                  <input
                    type="number"
                    step="any"
                    placeholder="e.g. 100"
                    value={manualQuantity}
                    onChange={(event) => setManualQuantity(event.target.value)}
                    className="w-full rounded-lg border border-white/10 bg-black/40 px-2.5 py-1.5 text-xs text-white placeholder:text-muted/50 focus:border-neon focus:outline-hidden"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-muted mb-1 font-semibold uppercase">Price (Optional)</label>
                  <input
                    type="number"
                    step="any"
                    placeholder="e.g. 125.50"
                    value={manualPrice}
                    onChange={(event) => setManualPrice(event.target.value)}
                    className="w-full rounded-lg border border-white/10 bg-black/40 px-2.5 py-1.5 text-xs text-white placeholder:text-muted/50 focus:border-neon focus:outline-hidden"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={!manualSymbol.trim() || submittingSymbol !== null}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-neon/30 bg-neon/10 px-3 py-1.5 text-xs font-bold text-neon hover:bg-neon/20 transition-all disabled:opacity-50"
                >
                  {submittingSymbol === manualSymbol.trim().toUpperCase() ? (
                    <Loader2 size={14} className="animate-spin text-neon" />
                  ) : (
                    <Plus size={14} />
                  )}
                  Save Record
                </button>
              </div>
            </form>

            <div>
              <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted">Current broker stock positions</div>
              {stockPositions.length === 0 ? (
                <div className="rounded-xl border border-white/10 bg-black/20 px-3 py-4 text-center text-xs text-muted">No active stock positions in broker account. Use the form above to add a manual position.</div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-white/10">
                  {stockPositions.map((position) => {
                    const existing = previousPositions.some((item) => item.symbol === position.symbol);
                    const isSubmitting = submittingSymbol === position.symbol;
                    return (
                      <div key={`${position.symbol}-${position.contract_key || 'stock'}`} className="flex items-center justify-between gap-3 border-b border-white/5 px-3 py-2 last:border-b-0">
                        <div className="min-w-0">
                          <div className="font-semibold text-white">{position.symbol}</div>
                          <div className="text-[10px] text-muted">{position.quantity} shares · {position.current_price ? formatNQMMPrice(position.current_price) : 'price unavailable'}</div>
                        </div>
                        <button
                          type="button"
                          disabled={isSubmitting}
                          onClick={() => addPreviousPosition(position)}
                          className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-neon/30 bg-neon/10 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-neon hover:bg-neon/20 disabled:opacity-50"
                        >
                          {isSubmitting ? <Loader2 size={13} className="animate-spin text-neon" /> : existing ? <Check size={13} /> : <Plus size={13} />}
                          {existing ? 'Correct' : 'Add'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted">Recorded Previous 10 Positions</div>
              {previousPositions.length === 0 ? (
                <div className="rounded-xl border border-white/10 bg-black/20 px-3 py-5 text-center text-xs text-muted">No manual corrections recorded yet.</div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-white/10">
                  {previousPositions.map((position) => {
                    const isDeleting = submittingSymbol === position.symbol;
                    return (
                      <div key={position.id} className="flex items-center justify-between gap-3 border-b border-white/5 px-3 py-2 last:border-b-0">
                        <div>
                          <span className="font-semibold text-white">{position.symbol}</span>
                          <span className="ml-2 text-xs text-muted">{position.quantity} shares</span>
                          {position.current_price ? <span className="ml-2 text-[10px] text-muted">({formatNQMMPrice(position.current_price)})</span> : null}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] text-muted">{position.recorded_at ? formatNQMMDate(position.recorded_at) : '—'}</span>
                          <button
                            type="button"
                            disabled={isDeleting}
                            onClick={() => deletePreviousPosition(position.symbol)}
                            className="text-muted hover:text-rose-300 disabled:opacity-50"
                            aria-label={`Remove ${position.symbol}`}
                          >
                            {isDeleting ? <Loader2 size={14} className="animate-spin text-rose-300" /> : <Trash2 size={14} />}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default NQMMHistoryCorrectionModal;
